import { useEffect, useCallback, useMemo, useState } from 'react';
import { useSearchParams, useParams, NavLink } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectListAttendeesLoading,
  selectListAttendeesPage,
  selectListAttendeesPageSize,
  selectListAttendeesSort,
  selectListAttendeesSortDirection,
  selectListAttendeesTotal,
  selectListAttendeesUsers,
} from '@/store/slices/audiences/selectors';
import {
  setListAttendeesFields,
  setListAttendeesPage,
  setListAttendeesPageSize,
  setListAttendeesSort,
  setListAttendeesSearch,
  resetListAttendees,
  fetchListAttendees,
} from '@/store/slices/audiences';
import { fetchPasscodeList } from '@/store/slices/passcode-lists';
import { selectPasscodeList } from '@/store/slices/passcode-lists/selectors';
import { AttendeeUserDefault } from '@/types/all-attendees.types';
import type { RegistrationFilter } from '@/types/all-attendees.types';
import Table from '@layouts/layout-table/layout-table';
import TableBody from '@layouts/layout-table/layout-table-body';
import OptionalComponent from '@ui/optional-component/optional-component';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import SearchInput from '@ui/search-input/search-input';
import useFixedDrawer from '@ui/fixed-drawer/use-fixed-drawer';
import { useLoadListAttendees } from '../../hooks/listsv2/use-load-list-attendees';
import {
  buildAttendeeFields,
  DEFAULT_COLUMN_IDS_WEBINAR,
  DEFAULT_COLUMN_IDS_TOWNHALL,
} from './profile-column-definitions';
import ProfileListRow from './profile-list-row';
import ProfileColumnsSelect from './profile-columns-select';
import AddUserToList from './add-user-to-list';
import AllProfilesBulkActions from './all-profiles-bulk-actions';
import NoProfiles from './no-profiles';
import CampaignUsageDropdown from './campaign-usage-dropdown';
import ProfilesRegistrationStatusFilter from './profiles-registration-status-filter';
import CreateListModal from './create-list-modal';
import ProfileTableHead from './profile-table-head';
import ProfileTableFooter from './profile-table-footer';
import { selectWorkingProjectRegistrationQuestions, selectWorkingProjectUuid } from '@/store/slices/project/selectors';
import { selectActiveChannel, selectUserChannel } from '@/store/slices/user/selectors';
import { isWebinarChannel } from '@/utils/project-types/project-type-guards';
import { DeletePasscodeListMembers } from '@/connection/audiences/passcode-lists';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';
import { UploadListCompletePayload } from '@/types/registration.types';
import socketManager from '@/connection/socket/socket-main-thread/socket-manager';
import { getDisplayName } from '../../utils';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { useColumnState } from '../../hooks/listsv2/use-column-state';
import { useProfileSelection } from '../../hooks/listsv2/use-profile-selection';
import { useVisibleColumns } from '../../hooks/listsv2/use-visible-columns';

const REGISTRATION_FILTERS: RegistrationFilter[] = ['registered', 'invited', 'added'];

const AudienceList = () => {
  const params = useParams() as { passcodeListId?: string; projectUuid?: string; language?: string };
  const passcodeListId = Number(params.passcodeListId) || 0;

  const dispatch = useAppDispatch();
  const channel = useAppSelector(selectActiveChannel);
  const projectUuid = useAppSelector(selectWorkingProjectUuid);
  const passcodeList = useAppSelector(selectPasscodeList);
  const userChannel = useAppSelector(selectUserChannel);
  const registrationFields = useAppSelector(selectWorkingProjectRegistrationQuestions);
  const users = useAppSelector(selectListAttendeesUsers);
  const loading = useAppSelector(selectListAttendeesLoading);
  const total = useAppSelector(selectListAttendeesTotal);
  const page = useAppSelector(selectListAttendeesPage);
  const pageSize = useAppSelector(selectListAttendeesPageSize);
  const currentSort = useAppSelector(selectListAttendeesSort);
  const currentSortDirection = useAppSelector(selectListAttendeesSortDirection);
  const totalPages = Math.ceil(total / Number(pageSize));

  // the loaded list can briefly be the previously viewed one - only trust it once it matches the route
  const passcodeListUuid = passcodeList?.passcode_list === passcodeListId ? passcodeList.uuid : undefined;

  const listName = useMemo(() => {
    if (passcodeList?.passcode_list === passcodeListId) {
      return passcodeList.name ? getDisplayName(passcodeList.name) : 'Audience';
    }
    return 'Audience';
  }, [passcodeList, passcodeListId]);

  useEffect(() => {
    if (channel && passcodeListId) {
      dispatch(fetchPasscodeList({ channel, passcodeListId }));
    }
  }, [channel, passcodeListId, dispatch]);

  const [searchParams, setSearchParams] = useSearchParams();

  const registrationParam = searchParams.get('registrationStatus');
  const registrationStatusFilter = useMemo(
    () =>
      REGISTRATION_FILTERS.includes(registrationParam as RegistrationFilter)
        ? (registrationParam as RegistrationFilter)
        : undefined,
    [registrationParam]
  );

  useLoadListAttendees(passcodeListId, registrationStatusFilter);

  useEffect(() => {
    return () => {
      dispatch(resetListAttendees());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!channel || !passcodeListId) return;
    const namespace = `admin-${channel}`;
    const socket = socketManager.getFromHook(namespace);
    const handleComplete = (response: UploadListCompletePayload) => {
      if (response.data.passcodeListId === passcodeListId) {
        dispatch(fetchListAttendees({ passcodeListId, registrationStatus: registrationStatusFilter }));
      }
    };
    socket.addListener<UploadListCompletePayload>('upload-list-complete', handleComplete);
    return () => {
      socket.removeListener('upload-list-complete', handleComplete);
      socketManager.leave(namespace);
    };
  }, [channel, passcodeListId, registrationStatusFilter, dispatch]);

  const defaultColumnIds = useMemo(
    () => (isWebinarChannel(userChannel?.type) ? DEFAULT_COLUMN_IDS_WEBINAR : DEFAULT_COLUMN_IDS_TOWNHALL),
    [userChannel?.type]
  );

  const { setOpenDrawer } = useFixedDrawer({ id: 'profile-columns-select' });
  const storageKey = `list-cols-${passcodeListId}`;
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);

  const handleFieldsChange = useCallback(
    (cols: string[]) => dispatch(setListAttendeesFields(buildAttendeeFields(cols))),
    [dispatch]
  );

  const { enabledColumns, columnOrder, handleColumnToggle, handleColumnReorder } = useColumnState({
    storageKey,
    searchParams,
    setSearchParams,
    defaultColumnIds,
    onFieldsChange: handleFieldsChange,
  });

  const {
    selectedProfiles,
    addToListPopover,
    displayedUsers,
    selectedEmails,
    allDisplayedSelected,
    someDisplayedSelected,
    handleSelectAll,
    handleSelectOne,
    handleAddSingleUserToList,
    handleSaveAddToList,
    clearSelection,
    getHeight,
  } = useProfileSelection({ users: users as AttendeeUserDefault[], channel: channel ?? undefined, projectUuid });

  const visibleColumns = useVisibleColumns(columnOrder, enabledColumns, registrationFields);

  const handleSort = useCallback(
    (sortKey: string) => () => {
      const nextDirection = currentSort === sortKey && currentSortDirection === 'asc' ? 'desc' : 'asc';
      dispatch(setListAttendeesSort({ sort: sortKey, sortDirection: nextDirection }));
    },
    [currentSort, currentSortDirection, dispatch]
  );

  const handleRemoveFromList = useCallback(async () => {
    if (!channel || !passcodeListId || !selectedProfiles.length) return;
    const selectedEmails = new Set(selectedProfiles.map((p) => p['3']));
    const blProfiles = displayedUsers
      .filter((u) => selectedEmails.has(u.email))
      .map((u) => u.bl_profile)
      .filter((id): id is number => id !== null);
    if (!blProfiles.length) return;
    try {
      await DeletePasscodeListMembers({ bl_profiles: blProfiles, passcode_list: passcodeListId, channel });
      clearSelection();
      dispatch(fetchListAttendees({ passcodeListId }));
      toastTrigger(`${blProfiles.length} user${blProfiles.length === 1 ? '' : 's'} removed from list`, {
        type: EToastTypes.success,
        autoClose: 5000,
      });
    } catch (e) {
      console.error(e);
      toastTrigger('Failed to remove users from list', { type: EToastTypes.error, autoClose: 5000 });
    }
  }, [channel, passcodeListId, selectedProfiles, displayedUsers, clearSelection, dispatch]);

  return (
    <Container
      data-component="AudienceList"
      className="flex fd-column overflow-hidden"
      height="calc(100vh - 136px)"
      minHeight="calc(100vh - 136px)"
    >
      {/* Header */}
      <Container className="flex ai-center jc-space-between margin-b-16" height="32px">
        <div className="flex ai-center">
          <NavLink
            className="flex ai-center gap-8"
            to={`/project/edit/${params.projectUuid}/${params.language}/audiences/lists`}
          >
            <Icon name={ICONS.ARROW_LEFT} size={20} color="var(--global-ico-text-1)" />
            <Text className="H4" design="GlobalIcoText1">
              {listName}
            </Text>
          </NavLink>
          <CampaignUsageDropdown passcodeListUuid={passcodeListUuid} />
        </div>

        <Container className="flex ai-center gap-8">
          <SearchInput
            onSubmit={(search) => dispatch(setListAttendeesSearch(search))}
            onClear={() => dispatch(setListAttendeesSearch(''))}
            expandOnFocus
            width="300px"
            trackInParams
            debounceTime={500}
            isClearable
            isBorderless
          />

          <ProfilesRegistrationStatusFilter />

          <Button
            design="LocalBttnSec"
            onClick={setOpenDrawer}
            className="flex fd-row jc-center ai-center radius-full padding-0"
            width="32px"
            height="32px"
          >
            <Icon name={ICONS.COLUMN_SETTINGS} size={12} />
          </Button>

          <Button
            onClick={() => setShowAddMembersModal(true)}
            className="flex ai-center gap-6 padding-h-12"
            height="32px"
            backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
          >
            <Icon
              name={ICONS.ADD}
              size={12}
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            />
            <Text
              className="S2"
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            >
              Add members
            </Text>
          </Button>
        </Container>
      </Container>

      <AllProfilesBulkActions
        selectedCount={selectedProfiles.length || 0}
        onClearSelection={clearSelection}
        onAddToList={addToListPopover.setOpenPopover}
        onRemoveFromList={handleRemoveFromList}
      />
      <AddUserToList onSave={handleSaveAddToList} />

      <Container minHeight={getHeight()} height={getHeight()}>
        <OptionalComponent display={(!users || !users.length) && !loading}>
          <NoProfiles isAllProfiles={false} />
        </OptionalComponent>
        <OptionalComponent display={!!users && users.length > 0 && !loading}>
          <Container style={{ overflow: 'auto' }} minHeight={getHeight()} height={getHeight()}>
            <Table>
              <ProfileTableHead
                visibleColumns={visibleColumns}
                allDisplayedSelected={allDisplayedSelected}
                someDisplayedSelected={someDisplayedSelected}
                onSelectAll={handleSelectAll}
                currentSort={currentSort}
                currentSortDirection={currentSortDirection}
                onSort={handleSort}
              />
              <TableBody>
                {displayedUsers.map((user) => (
                  <ProfileListRow
                    key={`${user.bl_profile}-${user.email}`}
                    user={user}
                    visibleColumns={visibleColumns}
                    selected={selectedEmails.has(user.email)}
                    onSelect={handleSelectOne}
                    onAddClick={handleAddSingleUserToList}
                  />
                ))}
              </TableBody>
            </Table>
          </Container>
        </OptionalComponent>
      </Container>

      <ProfileTableFooter
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => dispatch(setListAttendeesPage(p))}
        onPageSizeChange={(size) => dispatch(setListAttendeesPageSize(size))}
        selectId="audience-list-page-count"
      />

      <ProfileColumnsSelect
        enabledColumns={enabledColumns}
        columnOrder={columnOrder}
        onToggle={handleColumnToggle}
        onReorder={handleColumnReorder}
      />

      <CreateListModal
        open={showAddMembersModal}
        setOpen={setShowAddMembersModal}
        passcodeListId={passcodeListId}
        onSaveComplete={() => dispatch(fetchListAttendees({ passcodeListId }))}
      />
    </Container>
  );
};

export default AudienceList;
